import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { getTracks, markReviewed } from '../api';
import useAuthStore from '../store/authStore';
import NavControls from '../components/NavControls';
import TrackTable from '../components/TrackTable';

const STORAGE_KEY = 'new_imports_since';
const MODE_KEY    = 'new_imports_mode';   // 'since' | 'unreviewed'

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function loadSinceDate() {
  return localStorage.getItem(STORAGE_KEY) || todayStr();
}

function loadMode() {
  return localStorage.getItem(MODE_KEY) || 'since';
}

export default function NewImports() {
  const { user } = useAuthStore();
  const isAdmin   = user?.is_staff === true;
  const queryClient = useQueryClient();

  const [mode, setMode]           = useState(loadMode);
  const [sinceDate, setSinceDate] = useState(loadSinceDate);
  const [editing, setEditing]     = useState(false);
  const [draftDate, setDraftDate] = useState(sinceDate);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [markingAll, setMarkingAll] = useState(false);
  const [selectedTracks, setSelectedTracks] = useState([]);

  useEffect(() => {
    const handler = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const switchMode = (m) => {
    setMode(m);
    localStorage.setItem(MODE_KEY, m);
  };

  const saveSince = (date) => {
    setSinceDate(date);
    localStorage.setItem(STORAGE_KEY, date);
    setEditing(false);
  };

  const queryKey = mode === 'unreviewed'
    ? ['new-imports', 'unreviewed']
    : ['new-imports', 'since', sinceDate];

  const {
    data: tracksData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    refetch,
  } = useInfiniteQuery(
    queryKey,
    ({ pageParam = 1 }) => {
      const params = { ordering: '-date_added', page_size: 100, page: pageParam };
      if (mode === 'unreviewed') {
        params.is_reviewed = 'false';
      } else {
        params.date_added__gte = new Date(sinceDate).toISOString();
      }
      return getTracks(params).then(r => r.data);
    },
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next, window.location.origin);
          return parseInt(url.searchParams.get('page'), 10);
        }
        return undefined;
      },
      keepPreviousData: true,
      staleTime: 30 * 1000,
    }
  );

  const tracks = useMemo(
    () => tracksData?.pages?.flatMap(p => p.results) ?? [],
    [tracksData]
  );
  const totalCount = tracksData?.pages?.[0]?.count ?? 0;

  const handleMarkReviewed = useCallback(async (trackId) => {
    await markReviewed(trackId);
    queryClient.invalidateQueries(queryKey);
  }, [queryClient, queryKey]);

  const handleMarkSelectedReviewed = async () => {
    if (!selectedTracks.length) return;
    setMarkingAll(true);
    try {
      await Promise.all(selectedTracks.map(t => markReviewed(t.id)));
      queryClient.invalidateQueries(['new-imports']);
    } finally {
      setMarkingAll(false);
    }
  };

  const listHeight = Math.max(200, windowHeight - 320);

  const formattedSince = new Date(sinceDate + 'T00:00:00').toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  const radioStyle = (active) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '7px 18px', borderRadius: 20,
    border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
    background: active ? 'var(--accent)' : 'var(--bg2)',
    color: active ? '#fff' : 'var(--text)',
    cursor: 'pointer', fontSize: 13,
    fontWeight: active ? 600 : 400,
    userSelect: 'none',
  });

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1>New Music</h1>
          <NavControls />
        </div>
        <p>
          {isLoading
            ? 'Loading…'
            : `${totalCount} track${totalCount !== 1 ? 's' : ''}`}
          {isFetching && !isLoading && (
            <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>· Loading more…</span>
          )}
        </p>
      </div>

      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: 8, padding: '0 20px 16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button style={radioStyle(mode === 'since')} onClick={() => switchMode('since')}>
          📅 All since date
        </button>
        <button style={radioStyle(mode === 'unreviewed')} onClick={() => switchMode('unreviewed')}>
          🔍 Unreviewed
        </button>

        {mode === 'unreviewed' && totalCount > 0 && isAdmin && (
          <button
            onClick={handleMarkSelectedReviewed}
            disabled={markingAll || selectedTracks.length === 0}
            style={{
              marginLeft: 'auto', padding: '7px 16px', fontSize: 13,
              background: 'none', border: '1px solid var(--border)',
              borderRadius: 20,
              color: selectedTracks.length > 0 ? 'var(--accent)' : 'var(--text-muted)',
              cursor: selectedTracks.length > 0 ? 'pointer' : 'default',
              opacity: selectedTracks.length > 0 ? 1 : 0.5,
            }}
          >
            {markingAll
              ? '…'
              : selectedTracks.length > 0
                ? `✓ Mark ${selectedTracks.length} reviewed`
                : '✓ Mark reviewed (select tracks)'}
          </button>
        )}
      </div>

      {/* Since-date controls (only in 'since' mode) */}
      {mode === 'since' && isAdmin && (
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '0 20px 16px' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            All music imported since
          </span>
          {editing ? (
            <>
              <input
                type="date"
                value={draftDate}
                onChange={e => setDraftDate(e.target.value)}
                style={{
                  background: 'var(--bg2)', border: '1px solid var(--accent)',
                  borderRadius: 6, color: 'var(--text)', padding: '4px 8px', fontSize: 14,
                }}
              />
              <button className="btn" style={{ padding: '4px 14px', fontSize: 13 }} onClick={() => saveSince(draftDate)}>
                Save
              </button>
              <button
                style={{ padding: '4px 14px', fontSize: 13, background: 'none', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-muted)', cursor: 'pointer' }}
                onClick={() => { setDraftDate(sinceDate); setEditing(false); }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => { setDraftDate(sinceDate); setEditing(true); }}
                style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--accent)', cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: '4px 14px' }}
              >
                {formattedSince} ✎
              </button>
            </>
          )}
        </div>
      )}

      {isLoading ? (
        <p style={{ color: 'var(--text-muted)', padding: '20px' }}>Loading…</p>
      ) : tracks.length === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>
            {mode === 'unreviewed' ? '✅' : '📭'}
          </div>
          <div>
            {mode === 'unreviewed'
              ? 'All tracks have been reviewed.'
              : `No tracks imported since ${formattedSince}.`}
          </div>
          {mode === 'since' && (
            <div style={{ fontSize: 13, marginTop: 6 }}>
              Drop files in your imports folder and run an import, or adjust the date above.
            </div>
          )}
        </div>
      ) : (
        <TrackTable
          tracks={tracks}
          queryKey={queryKey}
          editable={isAdmin}
          deletable={isAdmin ? 'library' : null}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingMore={isFetching && !isLoading}
          listHeight={listHeight}
          storageKey="musicapp_columns_new_imports"
          defaultColumns={['index', 'track_number', 'title', 'artist_name', 'album_title', 'date_added', 'duration']}
          onMarkReviewed={mode === 'unreviewed' && isAdmin ? handleMarkReviewed : null}
          onSelectionChange={mode === 'unreviewed' && isAdmin ? setSelectedTracks : null}
        />
      )}
    </div>
  );
}
